import React, { useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import type { MarketingSource } from '@troia-v3/types';
import {
  LEAD_SOURCES,
  LEAD_SOURCE_LABELS,
  getLeadSourceLabel,
  isLeadSource,
} from '@troia-v3/types';

import { useFunnelsDropdown } from '@/modules/funnels/hooks/useFunnels';
import { FiltersButton } from '@/shared/components/ui/FiltersButton';
import { Select, type SelectOption } from '@/shared/components/ui/Select';

interface MarketingFilterProps {
  funnelId: string | null;
  source: MarketingSource | null;
  onFunnelChange: (funnelId: string | null) => void;
  onSourceChange: (source: MarketingSource | null) => void;
}

/**
 * Origens do dropdown — derivadas do enum canônico `LEAD_SOURCES` em
 * `@troia-v3/types` (single source of truth).
 */
const SOURCE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Todas as origens' },
  ...LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] })),
];

/** Override de CSS vars → dropdown/popover SEMPRE em tema claro fixo. */
const LIGHT_POPOVER_VARS: React.CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-card-muted': '#94a3b8',
  '--theme-primary': '#8b5cf6',
  '--theme-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-component-background': '#ffffff',
} as React.CSSProperties;

const useFunnelName = (funnelId: string | null): string | null => {
  const { data: funnels } = useFunnelsDropdown();
  if (!funnelId) return null;
  return funnels?.find((f) => f.id === funnelId)?.name ?? null;
};

/**
 * Botão "Filtros" (controle da toolbar, zona direita) — abre um popover claro
 * fixo com os selects de Funil + Origem. Badge mostra a contagem de filtros
 * ativos. Substitui os selects inline (padrão travado 2026-07-19).
 */
export const MarketingFilterButton: React.FC<MarketingFilterProps> = ({
  funnelId,
  source,
  onFunnelChange,
  onSourceChange,
}) => {
  const { data: funnels, isLoading: funnelsLoading } = useFunnelsDropdown();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const activeCount = (funnelId ? 1 : 0) + (source ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const funnelOptions: SelectOption[] = [
    { value: '', label: funnelsLoading ? 'Carregando funis…' : 'Todos os funis' },
    ...(funnels ?? []).map((f) => ({ value: f.id, label: f.name })),
  ];

  return (
    <div className="relative" ref={wrapperRef} data-testid="marketing-filters-bar">
      {/* Botão Filtros canônico do DS (F05 — shared FiltersButton) */}
      <FiltersButton
        onClick={() => setOpen((prev) => !prev)}
        count={activeCount}
        active={open}
        data-testid="marketing-filters-button"
      />

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50 rounded-lg shadow-lg p-3"
          style={{ width: 280, ...LIGHT_POPOVER_VARS }}
          role="dialog"
          aria-label="Filtros do dashboard"
          data-testid="marketing-filters-popover"
        >
          <div className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: '#94a3b8' }}>
            Filtros
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                Funil
              </label>
              <Select
                options={funnelOptions}
                value={funnelId ?? ''}
                onChange={(v) => onFunnelChange(v === '' ? null : v)}
                aria-label="Filtrar por funil"
                data-testid="marketing-filter-funnel"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#374151' }}>
                Origem
              </label>
              <Select
                options={SOURCE_OPTIONS}
                value={source ?? ''}
                onChange={(v) => onSourceChange(v === '' || !isLeadSource(v) ? null : v)}
                aria-label="Filtrar por origem"
                data-testid="marketing-filter-source"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MarketingFilterButton.displayName = 'MarketingFilterButton';

interface ChipProps {
  label: string;
  onRemove: () => void;
  testId: string;
}

const FilterChip: React.FC<ChipProps> = ({ label, onRemove, testId }) => (
  /* `.fchip` do DS 3.3 — 26px, tokens primário (F63: sem hex hardcoded) */
  <span
    className="inline-flex items-center gap-1 h-[26px] pl-[11px] pr-[5px] rounded-full text-xs font-semibold"
    style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
    data-testid={testId}
  >
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="inline-flex items-center justify-center rounded-full"
      style={{ width: 18, height: 18, opacity: 0.7 }}
      aria-label={`Remover filtro ${label}`}
      data-testid={`${testId}-remove`}
    >
      <FiX size={13} />
    </button>
  </span>
);

/**
 * Chips de filtros ativos (funil/origem) — removíveis + "Limpar tudo".
 * Renderiza `null` quando não há filtro (a filterbar some, padrão travado).
 */
export const MarketingFilterChips: React.FC<MarketingFilterProps> = ({
  funnelId,
  source,
  onFunnelChange,
  onSourceChange,
}) => {
  const funnelName = useFunnelName(funnelId);
  const hasAny = Boolean(funnelId) || Boolean(source);

  if (!hasAny) return null;

  return (
    <div
      className="flex items-center flex-wrap gap-2 px-4 sm:px-6 md:px-8 py-2.5"
      style={{
        backgroundColor: 'var(--theme-header-background)',
        borderBottom: '1px solid var(--theme-header-border)',
      }}
      data-testid="marketing-filter-chips"
    >
      {funnelId && (
        <FilterChip
          label={`Funil: ${funnelName ?? '…'}`}
          onRemove={() => onFunnelChange(null)}
          testId="marketing-chip-funnel"
        />
      )}
      {source && (
        <FilterChip
          label={`Origem: ${getLeadSourceLabel(source)}`}
          onRemove={() => onSourceChange(null)}
          testId="marketing-chip-source"
        />
      )}
      <button
        type="button"
        onClick={() => {
          onFunnelChange(null);
          onSourceChange(null);
        }}
        className="text-xs font-semibold"
        style={{ color: 'var(--theme-muted-text)' }}
        data-testid="marketing-filters-clear-all"
      >
        Limpar tudo
      </button>
    </div>
  );
};

MarketingFilterChips.displayName = 'MarketingFilterChips';
